import { useCallback, useEffect, useState } from "react";

import { toast } from "sonner";

import { useApi } from "../../../context/ApiContext";
import { useConfirm } from "../../../context/ConfirmContext.jsx";
import { usePagination } from "../../../hooks/usePagination";

export const useTaskApi = (initialPageSize = 10, initialProjectId = "") => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProject, setSelectedProject] = useState(initialProjectId);

  useEffect(() => {
    setSelectedProject(initialProjectId);
  }, [initialProjectId]);

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [milestoneFilter, setMilestoneFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("createdAt");
  const [direction, setDirection] = useState("desc");
  const [showDeleted, setShowDeleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [statusCounts, setStatusCounts] = useState({
    PENDING: 0,
    IN_PROGRESS: 0,
    COMPLETED: 0,
    OVERDUE: 0,
    ALL: 0
  });

  const pagination = usePagination(initialPageSize);
  const { currentPage: page, pageSize, setPaginationData, items: tasks, setItems: setTasks } = pagination;

  const api = useApi();

  const fetchTasks = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await api.get(`/tasks`, {
        params: {
          projectId: selectedProject || undefined,
          status: statusFilter !== "ALL" ? statusFilter : undefined,
          milestoneId: milestoneFilter !== "ALL" ? milestoneFilter : undefined,
          page,
          size: pageSize,
          search: searchTerm?.trim() || undefined,
          sortBy,
          direction,
          showDeleted: showDeleted
        }
      });
      setPaginationData(response.data);

      const counts = response.data.statusCounts || response.data.metadata?.statusCounts;
      if (counts) {
        const total = (counts.PENDING || 0) + (counts.IN_PROGRESS || 0) + (counts.COMPLETED || 0) + (counts.CANCELLED || 0);
        setStatusCounts({ ...counts, ALL: total });
      }
      return response.data;
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
      toast.error(error.message || "Failed to fetch tasks");
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [api, page, pageSize, searchTerm, selectedProject, statusFilter, milestoneFilter, sortBy, direction, showDeleted, setPaginationData]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const createTask = async (taskData) => {
    try {
      await api.post(`/tasks`, taskData);
      fetchTasks();
      toast.success("Task created successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to create task");
      throw error;
    }
  };

  const updateTask = async (taskId, updatedData) => {
    try {
      const response = await api.put(`/tasks/${taskId}`, updatedData);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? response.data : t)));
      toast.success("Task updated successfully!");
      return response.data;
    } catch (error) {
      toast.error(error.message || "Failed to update task");
      throw error;
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await api.put(`/tasks/${taskId}/status`, { status });
      // Update local state for immediate feedback
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status } : t)));
      toast.success("Task status updated!");
      // Also refresh to get updated counts
      fetchTasks();
    } catch (error) {
      toast.error(error.message || "Failed to update task status");
    }
  };

  const reassignTask = async (taskId, assignedTo) => {
    try {
      await api.put(`/tasks/${taskId}/assign`, { assignedTo: assignedTo });
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, assignedTo: { id: assignedTo } } : t)));
      toast.success("Task reassigned successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to reassign task");
    }
  };

  const confirm = useConfirm();

  const deleteTask = async (taskId) => {
    const isConfirmed = await confirm({
      title: "Delete Task",
      message: "Are you sure you want to delete this task?"
    });
    if (isConfirmed) {
      try {
        await api.delete(`/tasks/${taskId}`);
        fetchTasks();
        toast.success("Task deleted successfully!");
        return true;
      } catch (error) {
        toast.error(error.message || "Failed to delete task");
        return false;
      }
    }
    return false;
  };

  const getTaskById = async (taskId) => {
    try {
      const response = await api.get(`/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      toast.error(error.message || "Failed to fetch task details");
      throw error;
    }
  };

  return {
    tasks,
    pagination,
    searchTerm,
    setSearchTerm,
    selectedProject,
    setSelectedProject,
    statusFilter,
    setStatusFilter,
    milestoneFilter,
    setMilestoneFilter,
    statusCounts,
    showDeleted,
    setShowDeleted,
    isLoading,
    fetchTasks,
    createTask,
    updateTask,
    deleteTask,
    updateTaskStatus,
    reassignTask,
    getTaskById,
    sortBy,
    setSortBy,
    direction,
    setDirection
  };
};
